import React, { useState, useEffect } from "react";
import { FaX } from "react-icons/fa6";
import { IoCheckmark } from "react-icons/io5";
import { FiSend } from "react-icons/fi";
import { FaAngleDown } from "react-icons/fa6";
import Button from "../../../../components/button-v1/index";
import { Dropdown } from "../../../../components/ui/dropdown";
import styles from "./sendVideoModal.module.css";
import PatientService from "../../../../services/patient-service";
import VideoService from "../../../../services/video-service";

const SendModal = ({
  open,
  onClose,
  selectedVideos = [],
  allVideos = [],
  onToggleSelect,
  patients = [],
  currentPatient = null,
}) => {
  const [patientEmails, setPatientEmails] = useState("");
  const [clientEmails, setClientEmails] = useState("");
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [sendTo, setSendTo] = useState("patient");
  const [allPatients, setAllPatients] = useState(patients);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedVideoDetails, setSelectedVideoDetails] = useState([]);

  useEffect(() => {
    const loadPatients = async () => {
      if (patients.length === 0) {
        setIsLoadingPatients(true);
        try {
          const patientData = await PatientService.fetchAllPatients();
          const patientsList = patientData?.patients || [];
          setAllPatients(patientsList);
        } catch (error) {
          console.error("Error fetching patients:", error);
        } finally {
          setIsLoadingPatients(false);
        }
      }
    };
    
    if (open) {
      loadPatients();
      const details = selectedVideos
        .map((id) => allVideos.find((v) => v.uuid === id))
        .filter(Boolean);
      setSelectedVideoDetails(details);
      
      // Auto-populate current patient if available
      if (currentPatient && currentPatient.user && currentPatient.user.email) {
        const patientEmail = currentPatient.user.email;
        const patientName = `${currentPatient.user.first_name} ${currentPatient.user.last_name}`;
        setPatientEmails(patientEmail);
        setSelectedPatients([patientEmail]);
        setSendTo("patient");
      }
    }
  }, [open, patients.length, selectedVideos, allVideos, currentPatient]);

  if (!open) return null;

  const validateEmails = (text) => {
    if (!text) return [];
    return text
      .split(/[\n,;\s]+/)
      .map((e) => e.trim())
      .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
  };

  const patientValid = validateEmails(patientEmails);
  const clientValid = validateEmails(clientEmails);
  const totalRecipients = sendTo === "patient" ? patientValid.length : clientValid.length;

  const getVideoTitle = (id) =>
    allVideos.find((v) => v.uuid === id)?.title || "Unknown Video";

  const patientOptions = allPatients.map((patient) => ({
    value: patient.email,
    label: `${patient.first_name} ${patient.last_name} - ${patient.email}`,
    name: `${patient.first_name} ${patient.last_name}`,
  }));

  const recipientTypeOptions = [
    { value: "patient", label: "Patients" },
    { value: "client", label: "Clients" },
  ];

  const selectedTypeLabel = recipientTypeOptions.find((opt) => opt.value === sendTo)?.label || "Select recipient type";

  const handleSendToChange = (value) => {
    if (value === sendTo) return;
    if (value === "patient") {
      setClientEmails("");
    } else {
      setPatientEmails("");
      setSelectedPatients([]);
    }
    setSendTo(value);
  };

  const handlePatientSelection = (selectedEmail) => {
    if (selectedEmail && !selectedPatients.includes(selectedEmail)) {
      setSelectedPatients([...selectedPatients, selectedEmail]);
      // Add to patient emails textarea
      const currentEmails = patientEmails.trim();
      const newEmails = currentEmails ? `${currentEmails}, ${selectedEmail}` : selectedEmail;
      setPatientEmails(newEmails);
    }
  };
  const removeSelectedPatient = (emailToRemove) => {
    setSelectedPatients(selectedPatients.filter((email) => email !== emailToRemove));
    // Remove from patient emails textarea
    const emails = patientEmails.split(/[,\n\s]+/).filter((e) => e.trim() !== emailToRemove);
    setPatientEmails(emails.join(", "));
  };
  const handleSend = async () => {
    if (selectedVideos.length > 0 && totalRecipients > 0) {
      setIsSending(true);
      try {
        const validRecipients = sendTo === "patient" ? patientValid : clientValid;
        const result = await VideoService.sendVideos({
          videoIds: selectedVideos,
          videos: allVideos,
          recipients: validRecipients,
          recipientType: sendTo,
          patients: allPatients
        });
        
        if (result.success) {
          onClose();
        }
      } catch (error) {
        console.error("Error sending videos:", error);
      } finally {
        setIsSending(false);
      }
    }
  };
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.titleWrapper}>
            <FiSend className={styles.headerIcon} />
            <h2 className={styles.title}>Send Videos</h2>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <FaX />
          </button>
        </div>
        <div className={styles.body}>
          <div className={styles.field}>
            <label className={styles.label}>
              Selected Videos ({selectedVideos.length})
            </label>
            <div className={styles.chipsWrapper}>
              {selectedVideos.length === 0 ? (
                <p className={styles.emptyMessage}>
                  No videos selected
                </p>
              ) : (
                selectedVideos.map((id) => {
                  const video =
                    selectedVideoDetails.find((v) => v.uuid === id) ||
                    allVideos.find((v) => v.uuid === id);
                  return (
                    <div key={id} className={styles.videoChip}>
                      <div className={styles.chipContent}>
                        <span className={styles.checkmark}>
                          <IoCheckmark />
                        </span>
                        <div className={styles.chipTextBlock}>
                          <span className={styles.chipTitle}>
                            {video?.title || getVideoTitle(id)}
                          </span>
                        </div>
                      </div>
                      <button
                        className={styles.chipRemove}
                        onClick={() => onToggleSelect(id)}
                      >
                        ×
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Send to</label>
            <Dropdown
              options={recipientTypeOptions}
              value={sendTo}
              onChange={handleSendToChange}
              className={styles.dropdown}
              trigger={
                <div className={styles.dropdownTrigger}>
                  <span className={styles.triggerText}>{selectedTypeLabel}</span>
                  <FaAngleDown className={styles.triggerArrow} />
                </div>
              }
            />
          </div>

          {sendTo === "patient" && (
            <>
              <div className={styles.field}>
                <label className={styles.label}>Select Patients</label>
                <Dropdown
                  options={patientOptions}
                  value=""
                  onChange={handlePatientSelection}
                  placeholder={isLoadingPatients ? "Loading patients..." : "Search and select patients"}
                  className={styles.dropdown}
                  disabled={isLoadingPatients}
                  searchable={true}
                  trigger={
                    <div className={styles.dropdownTrigger}>
                      <span className={styles.triggerText}>
                        {isLoadingPatients ? "Loading patients..." : "Search and select patients"}
                      </span>
                      <FaAngleDown className={styles.triggerArrow} />
                    </div>
                  }
                />
              </div>

              {selectedPatients.length > 0 && (
                <div className={styles.selectedPatients}>
                  <p className={styles.selectedLabel}>Selected Patients:</p>
                  {selectedPatients.map((email) => {
                    const patient = allPatients.find((p) => p.email === email);
                    return (
                      <div key={email} className={styles.patientChip}>
                        <span>{patient?.first_name && patient?.last_name ? `${patient.first_name} ${patient.last_name}` : patient?.name || email}</span>
                        <button
                          className={styles.removePatient}
                          onClick={() => removeSelectedPatient(email)}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className={styles.field}>
                <label className={styles.label}>Patient Email(s)</label>
                <textarea
                  value={patientEmails}
                  onChange={(e) => setPatientEmails(e.target.value)}
                  placeholder="Enter patient emails, separated by commas or new lines"
                  // rows={1}
                  className={styles.textarea}
                />
                <p className={styles.validCount}>
                  {patientValid.length} valid email{patientValid.length !== 1 ? "s" : ""}
                </p>
              </div>
            </>
          )}

          {sendTo === "client" && (
            <div className={styles.field}>
              <label className={styles.label}>Client Email(s)</label>
              <textarea
                value={clientEmails}
                onChange={(e) => setClientEmails(e.target.value)}
                placeholder="Enter client emails, separated by commas or new lines"
                rows={4}
                className={styles.textarea}
              />
              <p className={styles.validCount}>
                {clientValid.length} valid email{clientValid.length !== 1 ? "s" : ""}
              </p>
            </div>
          )}
        </div>
        <div className={styles.footer}>
          <p className={styles.fileNote}>Email sending requires Cloud Email</p>
          <div className={styles.buttonGroup}>
            <Button className={styles.cancelButton} onClick={onClose}>
              Cancel
            </Button>
            <Button
              className={styles.actionButton}
              onClick={handleSend}
              disabled={selectedVideos.length === 0 || totalRecipients === 0 || isSending}
            >
              {isSending ? "Sending..." : `Send to ${totalRecipients} Recipient${totalRecipients !== 1 ? "s" : ""}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendModal;